import { ChangeDetectionStrategy, Component, EventEmitter, OnInit, Output, inject } from '@angular/core';
import { AppFacadeService } from '../../../services/app-facade.service';
import { Customer } from '../../../models/consumer';
import { combineLatest, finalize, map, Subscription, switchMap, tap, withLatestFrom } from 'rxjs';
import { DialogContentType } from '../../../enums/dialog-type.enum';
import isEmpty from 'lodash-es/isEmpty';
import { PorPayEvent } from '../../models/paymentOutput';
import { AppEventService } from '../../../services/app-event.service';
import { OriginActionName } from '@por/shared/ui/cross-app';
import { select, Store } from '@ngrx/store';
import { selectRouteParamsTokenizeOnly } from '../../../store/app-wide/config/config.selectors';

@Component({
    standalone: false,
    selector: 'por-ecom-payment',
    templateUrl: './por-payment.component.html',
    styleUrls: ['./por-payment.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class PorPaymentComponent implements OnInit {
    readonly appFacadeService = inject(AppFacadeService);
    private readonly appEventService = inject(AppEventService);
    private readonly store = inject(Store);

    /**
     * Tokenize-only mode (AB#114949) - satisfies the "payment UI clearly indicates the card is
     * being saved for later payment" AC.
     *
     * Declared HERE and not on PorPaymentPageComponent: this component is what the 'Payment'
     * route renders (<por-ecom-payment>), i.e. the embedded payment component, which is the only
     * flow tokenize-only applies to. The payment-page dialog is the logged-in/QuickLink path and
     * never carries the claim.
     */
    tokenizeOnly$ = this.store.pipe(select(selectRouteParamsTokenizeOnly));

    /**
     * The notice appears only alongside the card form it describes:
     *
     * - not while the app-wide loader is spinning, so it does not sit on a still-initialising
     *   panel before there is anything to disclose against;
     * - not once the Card Saved dialog reports the outcome, because the notice is
     *   forward-looking ("will be saved... will not be charged") and would contradict the
     *   dialog's past tense.
     *
     * The dialog half is gated on the same pair of store values the success dialog itself
     * reads, so the two cannot disagree.
     */
    showTokenizeNotice$ = combineLatest([
        this.tokenizeOnly$,
        this.appFacadeService.getLoader(),
        this.appFacadeService.isDialogDisplayed(),
        this.appFacadeService.getDialogContentType()
    ]).pipe(
        map(
            ([tokenizeOnly, isLoading, dialogDisplayed, contentType]) =>
                tokenizeOnly && isLoading !== true && !(dialogDisplayed && contentType === DialogContentType.PaymentSuccess)
        )
    );

    @Output() readonly crossAppEvent: EventEmitter<PorPayEvent> = new EventEmitter<PorPayEvent>();

    subscriptions: Subscription[] = [];
    /**
     * Customer information needed, to prefill details on PoRPay page + to verify transaction
     */
    customer$ = this.appFacadeService.getCustomer().pipe(
        tap(() => this.appFacadeService.setLoading(true)),
        withLatestFrom(this.appFacadeService.getCustomerId()),
        switchMap(([customer, customerId]: [Customer, string | null]) => {
            if (isEmpty(customer) && customerId) {
                // Trigger fetch only if the customer is empty and an ID exists
                this.appFacadeService.fetchCustomerById(customerId);
                return this.appFacadeService.getCustomer(); // Re-fetch the customer
            }
            return [customer]; // Return current customer if not empty
        }),
        map((customer: Customer) => {
            if (!isEmpty(customer)) {
                // Only proceed with multiPaymentContinue when customer is successfully loaded
                this.appFacadeService.multiPaymentContinue();
            }
            return customer;
        }),
        finalize(() => {
            this.appFacadeService.setLoading(false);
        })
    );

    ngOnInit(): void {
        this.appFacadeService.fetchContractDetail(true);
        this.subscriptions.push(
            this.appFacadeService.getPaymentCrossAppEvent().subscribe(event => {
                if (event) {
                    this.crossAppEvent.emit(event);
                    this.appEventService.dispatch({
                        action: OriginActionName.PorPayResponse,
                        data: event
                    });
                }
            })
        );
    }
}
